import { useEffect, useState } from "react";
import { supabase } from "@/src/integrations/supabase/client";
import { usePortalAuth } from "@/lib/contexts/PortalAuthContext";

export type PartnerAccessTier = "temp" | "full";

interface State {
  tier: PartnerAccessTier | null;
  partnerId: string | null;
  loading: boolean;
}

/**
 * Resolves the current portal user's partner access tier.
 * Returns `loading: true` until a definitive answer is known.
 * If the user has no partner record yet, tier defaults to "temp"
 * (they should only see the temp portal until an admin promotes them).
 */
export function usePartnerAccessTier(): State {
  const { user, loading: authLoading } = usePortalAuth();
  const [state, setState] = useState<State>({ tier: null, partnerId: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!user) {
      setState({ tier: null, partnerId: null, loading: false });
      return;
    }

    (async () => {
      const { data, error } = await supabase
        .from("partners")
        .select("id, access_tier")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("Failed to load partner access tier:", error);
        setState({ tier: "temp", partnerId: null, loading: false });
        return;
      }

      setState({
        tier: (data?.access_tier as PartnerAccessTier) ?? "temp",
        partnerId: data?.id ?? null,
        loading: false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return state;
}
